import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { EmptyState, GlassButton, LoadingOverlay, PageHeader, PagePaper } from 'dashboard/components/common';

/**
 * The plan, and how to change it.
 *
 * The state worth getting right is the awkward one: a card that failed. The
 * brand is still on Business as far as they are concerned, the system is
 * honouring it until the grace period runs out, and the useful thing to show
 * is both facts at once — what they are paying for and what happens next —
 * rather than a bare "past due" that reads as an accusation.
 */

const LABELS = {
  brands: 'Brands',
  pages: 'Pages',
  products: 'Products',
  bookable_resources: 'Bookable things',
  email_boxes: 'Email boxes',
  custom_domain: 'Your own domain',
  remove_branding: 'No TechProOne badge',
};

const describe = (key, value) => {
  if (value === null || value === undefined) return 'Unlimited';
  if (value === true) return 'Included';
  if (value === false) return 'Not included';
  return String(value);
};

export default function Billing() {
  const [selectedBrand] = useOutletContext();
  const [state, setState] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    if (!selectedBrand) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      API.getBrandSubscription({ brand_id: selectedBrand }),
      API.listSubscriptionPlans({ brand_id: selectedBrand }),
    ])
      .then(([sub, list]) => { setState(sub); setPlans(list?.plans || []); })
      .catch((err) => toast.error(err?.message || 'Could not load your plan'))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => { load(); }, [load]);

  const choose = (plan) => {
    setBusy(plan.key);
    API.startSubscriptionCheckout({
      brand_id: selectedBrand,
      plan_key: plan.key,
      return_url: window.location.origin,
    })
      .then((res) => {
        // Straight to Stripe. Nothing is recorded here — the plan changes when
        // Stripe says it was paid for, not when someone opened the page.
        if (res?.url) window.location.assign(res.url);
        else toast.error('Could not start checkout');
      })
      .catch((err) => toast.error(err?.message || 'Could not start checkout'))
      .finally(() => setBusy(''));
  };

  const manage = () => {
    setBusy('portal');
    API.openBillingPortal({ brand_id: selectedBrand, return_url: window.location.origin })
      .then((res) => { if (res?.url) window.location.assign(res.url); })
      .catch((err) => toast.error(err?.message || 'Could not open billing'))
      .finally(() => setBusy(''));
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Plan and billing'
          subtitle='What this brand is on, and what it covers.'
          actions={state?.stripe_customer_id ? (
            <GlassButton onClick={manage} disabled={busy === 'portal'}>
              Payment details
            </GlassButton>
          ) : null}
        />

        {!selectedBrand ? (
          <EmptyState>Select a brand to see its plan.</EmptyState>
        ) : (
          <LoadingOverlay loading={loading} minHeight={200}>
            {/* The awkward state, said plainly. */}
            {state?.paid_plan && !state?.honoured && (
              <Box sx={{ p: '14px 16px', mb: '20px', borderRadius: '12px', bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                <Typography variant='body2' fontWeight='700'>
                  Your payment did not go through
                </Typography>
                <Typography variant='caption'>
                  This brand is on the Free plan until it is settled. Nothing has been deleted —
                  update the card and everything comes straight back.
                </Typography>
              </Box>
            )}
            {state?.cancel_at_period_end && state?.current_period_end && (
              <Box sx={{ p: '14px 16px', mb: '20px', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant='body2'>
                  Cancelled — this plan runs until{' '}
                  {new Date(state.current_period_end).toLocaleDateString()}, then returns to Free.
                </Typography>
              </Box>
            )}

            <Grid container spacing='14px'>
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} lg={3} key={plan.key}>
                  <Box
                    sx={{
                      height: '100%', p: '18px', borderRadius: '14px',
                      border: '1px solid',
                      borderColor: plan.current ? 'primary.main' : 'divider',
                      boxShadow: plan.current ? (t) => `0 0 0 1px ${t.palette.primary.main}` : 'none',
                      display: 'flex', flexDirection: 'column', gap: '10px',
                    }}
                  >
                    <Stack direction='row' alignItems='center' gap='8px'>
                      <Typography fontWeight='700'>{plan.name}</Typography>
                      {plan.current && <Chip size='small' color='primary' label='Current' />}
                    </Stack>
                    <Typography variant='h6' fontWeight='700'>{plan.price_label}</Typography>
                    <Typography variant='caption' color='text.secondary'>{plan.blurb}</Typography>

                    <Stack spacing='4px' mt='4px' sx={{ flex: 1 }}>
                      {Object.entries(plan.limits).map(([key, value]) => (
                        <Stack key={key} direction='row' alignItems='center' gap='6px'>
                          <CheckIcon
                            sx={{ fontSize: 15, color: value === false ? 'text.disabled' : 'success.main' }}
                          />
                          <Typography variant='caption' color={value === false ? 'text.disabled' : 'text.secondary'}>
                            {LABELS[key] || key}: {describe(key, value)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    {plan.current ? (
                      <GlassButton disabled disabledReason='This is the plan this brand is on.'>
                        Current plan
                      </GlassButton>
                    ) : (
                      <GlassButton
                        variant='contained'
                        onClick={() => choose(plan)}
                        disabled={!plan.purchasable || busy === plan.key}
                        // A plan with no price set up cannot be bought, and
                        // saying so beats a button that fails when pressed.
                        disabledReason={!plan.purchasable
                          ? 'This plan has no price set up yet — ask us about it.'
                          : 'Taking you to the payment page…'}
                      >
                        {plan.key === 'free' ? 'Downgrade' : `Choose ${plan.name}`}
                      </GlassButton>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </LoadingOverlay>
        )}
      </PagePaper>
    </Box>
  );
}
